export type BubbleLoaderVariant = "grow" | "spin" | "transparency";

/** Distances are in circle radii; speeds are cycles/revolutions per second. */
export const BUBBLE_LOADER_DEFAULTS = {
  colors: ["#7E51AB", "#172D82", "#7CC57F"] as [string, string, string],
  spacing: 2.27,
  neckWidth: 0.3, // Half-width: matches the selected balanced icon.
  speed: 1,
  grow: { speed: 0.5, minWidth: 0.12, maxWidth: 0.46 },
  spin: {
    enterDuration: 0.4, // Seconds; exit may extend slightly to settle at a full lobe.
    exitDuration: 0.5,
    restSpeed: 0,
    speed: 1.03, // Revolutions per second at full spin.
    restOutwardPull: 0.2,
    outwardPull: 0.43,
    restCompression: 0,
    compression: -0.52, // Negative compression expands the joins.
    blurPx: 1, // Inner/outer edge blur radius at full speed, in CSS pixels.
    restRingScale: 1,
    ringScale: 0.91,
    accentShiftPx: 5, // Signed CSS-pixel shift; positive moves right.
    accentJoinGrowthPx: 1, // Total join-width increase during the accent shift, in CSS pixels.
    accentMoveFraction: 0.2, // First 20% of entry; last 20% of exit.
  },
  transparency: {
    speed: 0.3,
    minOpacity: 0.2,
    trailWidth: 0.12,
  },
  maxPixelRatio: 2,
};

export type BubbleLoaderConfig = typeof BUBBLE_LOADER_DEFAULTS;

export function normalizeConfig(
  config: BubbleLoaderConfig,
): BubbleLoaderConfig {
  const clamp = (value: number, min: number, max: number, fallback: number) =>
    Math.max(min, Math.min(max, Number.isFinite(value) ? value : fallback));
  const defaults = BUBBLE_LOADER_DEFAULTS;
  const minWidth = clamp(
    config.grow.minWidth,
    0.04,
    0.65,
    defaults.grow.minWidth,
  );
  return {
    colors: config.colors.map((color, index) =>
      /^#[\da-f]{6}$/i.test(color) ? color : defaults.colors[index],
    ) as BubbleLoaderConfig["colors"],
    spacing: clamp(config.spacing, 2.1, 2.6, defaults.spacing),
    neckWidth: clamp(config.neckWidth, 0.04, 0.65, defaults.neckWidth),
    speed: clamp(config.speed, 0, 4, defaults.speed),
    grow: {
      speed: clamp(config.grow.speed, 0, 3, defaults.grow.speed),
      minWidth,
      maxWidth: clamp(
        config.grow.maxWidth,
        minWidth,
        0.7,
        defaults.grow.maxWidth,
      ),
    },
    spin: {
      enterDuration: clamp(
        config.spin.enterDuration,
        0.1,
        5,
        defaults.spin.enterDuration,
      ),
      exitDuration: clamp(
        config.spin.exitDuration,
        0.1,
        5,
        defaults.spin.exitDuration,
      ),
      restSpeed: clamp(config.spin.restSpeed, 0, 2, defaults.spin.restSpeed),
      restOutwardPull: clamp(
        config.spin.restOutwardPull,
        0,
        1.5,
        defaults.spin.restOutwardPull,
      ),
      restCompression: clamp(
        config.spin.restCompression,
        -0.8,
        0.8,
        defaults.spin.restCompression,
      ),
      restRingScale: clamp(
        config.spin.restRingScale,
        0.65,
        1,
        defaults.spin.restRingScale,
      ),
      accentShiftPx: clamp(
        config.spin.accentShiftPx,
        -20,
        20,
        defaults.spin.accentShiftPx,
      ),
      accentMoveFraction: clamp(
        config.spin.accentMoveFraction,
        0.01,
        1,
        defaults.spin.accentMoveFraction,
      ),
      accentJoinGrowthPx: clamp(
        config.spin.accentJoinGrowthPx,
        0,
        8,
        defaults.spin.accentJoinGrowthPx,
      ),
      blurPx: clamp(config.spin.blurPx, 0, 8, defaults.spin.blurPx),
      ringScale: clamp(config.spin.ringScale, 0.65, 1, defaults.spin.ringScale),
      speed: clamp(config.spin.speed, 0, 2, defaults.spin.speed),
      outwardPull: clamp(
        config.spin.outwardPull,
        0,
        1.5,
        defaults.spin.outwardPull,
      ),
      compression: clamp(
        config.spin.compression,
        -0.8,
        0.8,
        defaults.spin.compression,
      ),
    },
    transparency: {
      speed: clamp(
        config.transparency.speed,
        0,
        3,
        defaults.transparency.speed,
      ),
      minOpacity: clamp(
        config.transparency.minOpacity,
        0,
        1,
        defaults.transparency.minOpacity,
      ),
      trailWidth: clamp(
        config.transparency.trailWidth,
        0.03,
        0.35,
        defaults.transparency.trailWidth,
      ),
    },
    maxPixelRatio: clamp(config.maxPixelRatio, 1, 3, defaults.maxPixelRatio),
  };
}
